import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Card from '../../ui/Card.jsx';
import InfoTooltip from '../../ui/InfoTooltip.jsx';
import SegmentedControl from '../../components/SegmentedControl.jsx';
import { fetchPeopleOverview, fetchProjectsList } from '../../api.js';
import {
  PERIOD_OPTIONS,
  clamp01,
  formatHours,
  formatPct,
  initials,
} from './peopleMockData.js';

const STATUS_TABS = [
  { key: 'all', label: 'All' },
  { key: 'needsAttention', label: 'Needs Attention' },
  { key: 'overloaded', label: 'Overloaded' },
  { key: 'noLogs', label: 'No Logs' },
  { key: 'overdue', label: 'Overdue' },
];

const SORT_OPTIONS = [
  { key: 'attention', label: 'Attention order' },
  { key: 'overdue', label: 'Overdue' },
  { key: 'nologs', label: 'No logs' },
  { key: 'util', label: 'Utilization' },
];

function getAttentionGroup(person) {
  const { flags, underused } = person;
  if (flags.overloaded && flags.overdue) return 1;
  if (flags.overloaded) return 2;
  if (flags.overdue) return 3;
  if (flags.noLogs) return 4;
  if (underused) return 5;
  return 6;
}

function compareByAttention(a, b) {
  if (a.attentionGroup !== b.attentionGroup) return a.attentionGroup - b.attentionGroup;
  if (b.overdueCount !== a.overdueCount) return b.overdueCount - a.overdueCount;
  if (b.noLogDays !== a.noLogDays) return b.noLogDays - a.noLogDays;
  if (b.overload !== a.overload) return b.overload - a.overload;
  if (a.utilization !== b.utilization) return a.utilization - b.utilization;
  return String(a.name || '').localeCompare(String(b.name || ''));
}

function compareByKey(sortMode) {
  switch (sortMode) {
    case 'overdue':
      return (a, b) => (b.overdueCount - a.overdueCount) || compareByAttention(a, b);
    case 'nologs':
      return (a, b) => (b.noLogDays - a.noLogDays) || compareByAttention(a, b);
    case 'util':
      return (a, b) => (b.utilization - a.utilization) || compareByAttention(a, b);
    case 'attention':
    default:
      return compareByAttention;
  }
}

function Chip({ children, onClick, title }) {
  const interactive = typeof onClick === 'function';
  const Tag = interactive ? 'button' : 'span';
  return (
    <Tag
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      title={title}
      className={
        'inline-flex items-center rounded-full px-2 py-0.5 text-xs border ' +
        (interactive ? 'hover:opacity-90' : '')
      }
      style={{
        borderColor: 'var(--ui-border)',
        background: 'var(--ui-surface2)',
        color: 'var(--ui-text)',
      }}
    >
      {children}
    </Tag>
  );
}

function ProgressBar({ value }) {
  const pct = clamp01(value);
  return (
    <div
      className="h-3 rounded"
      style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }}
    >
      <div
        className="h-3 rounded"
        style={{
          width: `${Math.round(pct * 100)}%`,
          background: 'var(--ui-primary)',
        }}
      />
    </div>
  );
}

function ExportIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M12 3v10m0 0l4-4m-4 4l-4-4"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <path
        d="M4 14v5a2 2 0 002 2h12a2 2 0 002-2v-5"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function ChevronRightIcon() {
  return (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" aria-hidden="true">
      <path
        d="M9 18l6-6-6-6"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function PersonAvatar({ name, avatarUrl }) {
  const hasImg = Boolean(String(avatarUrl || '').trim());
  return (
    <div
      className="h-9 w-9 rounded-full flex items-center justify-center overflow-hidden"
      style={{ border: '1px solid var(--ui-border)', background: 'var(--ui-surface2)' }}
    >
      {hasImg ? (
        <img src={avatarUrl} alt={name || 'Avatar'} className="h-9 w-9 object-cover" />
      ) : (
        <span className="text-xs font-semibold" style={{ color: 'var(--ui-text-muted)' }}>
          {initials(name)}
        </span>
      )}
    </div>
  );
}

function KpiTile({ label, value, onClick }) {
  const interactive = typeof onClick === 'function';
  const Tag = interactive ? 'button' : 'div';
  return (
    <Tag
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      className={
        'flex flex-col items-start justify-center px-3 py-2 rounded ' +
        (interactive ? 'hover:opacity-90' : '')
      }
      style={{
        border: '1px solid var(--ui-border)',
        background: 'var(--ui-surface)',
        minWidth: 140,
        textAlign: 'left',
      }}
    >
      <div className="text-[11px]" style={{ color: 'var(--ui-text-muted)' }}>{label}</div>
      <div className="text-sm font-semibold" style={{ color: 'var(--ui-text)' }}>{value}</div>
    </Tag>
  );
}

export default function PeopleV2Page() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState('thisWeek');
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');
  const [projectId, setProjectId] = useState('all'); // 'all' | String(projectId)
  const [status, setStatus] = useState('needsAttention');
  const [searchText, setSearchText] = useState('');
  const [viewMode, setViewMode] = useState('table');
  const [sortMode, setSortMode] = useState('attention');

  const [projects, setProjects] = useState([]);
  const [overview, setOverview] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const rows = await fetchProjectsList();
        if (cancelled) return;
        setProjects(Array.isArray(rows) ? rows : []);
      } catch (_) {
        // Best-effort; keep dropdown usable with "All".
        if (cancelled) return;
        setProjects([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadOverview = useMemo(() => {
    return async () => {
      if (period === 'custom' && (!customStart || !customEnd)) {
        setOverview(null);
        setLoading(false);
        setLoadError('Custom period requires start and end');
        return;
      }
      setLoading(true);
      setLoadError('');
      try {
        const data = await fetchPeopleOverview({
          period,
          projectId: projectId === 'all' ? null : projectId,
          start: period === 'custom' ? customStart : undefined,
          end: period === 'custom' ? customEnd : undefined,
        });
        setOverview(data);
      } catch (err) {
        setOverview(null);
        setLoadError(err?.message || 'Failed to load people overview');
      } finally {
        setLoading(false);
      }
    };
  }, [period, projectId, customStart, customEnd]);

  function ensureCustomRangeDefault() {
    if (customStart && customEnd) return;
    const now = new Date();
    const day = now.getUTCDay();
    const mondayOffset = (day + 6) % 7;
    const monday = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate() - mondayOffset));
    const sunday = new Date(Date.UTC(monday.getUTCFullYear(), monday.getUTCMonth(), monday.getUTCDate() + 6));
    const toY = (d) => d.toISOString().slice(0, 10);
    setCustomStart(toY(monday));
    setCustomEnd(toY(sunday));
  }

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        await loadOverview();
      } finally {
        if (cancelled) return;
      }
    })();
    return () => { cancelled = true; };
  }, [loadOverview]);

  const people = useMemo(() => {
    const rows = overview && Array.isArray(overview.people) ? overview.people : [];
    return rows.map((p) => {
      const capacityHours = Number(p.capacityHours ?? p.capacity ?? 0);
      return {
        id: p.id,
        name: p.name,
        role: p.role || p.appRole || '',
        avatarUrl: p.avatarUrl || '',

        plannedHours: Number(p.plannedHours ?? 0),
        loggedHours: Number(p.loggedHours ?? 0),
        capacity: capacityHours,
        utilization: Number(p.utilization ?? 0),
        overload: Number(p.overload ?? 0),

        overdueCount: Number(p.overdueCount ?? 0),
        noLogDays: Number(p.noLogDays ?? 0),
        vacationDays: Number(p.vacationDays ?? 0),

        projectsCount: Number(p.projectsCount ?? 0),
        projectIds: Array.isArray(p.projectIds) ? p.projectIds : [],

        flags: p.flags || {
          overloaded: Number(p.overload ?? 0) > 1.1,
          nearCapacity: Number(p.overload ?? 0) >= 1.0 && Number(p.overload ?? 0) <= 1.1,
          overdue: Number(p.overdueCount ?? 0) > 0,
          noLogs: Number(p.noLogDays ?? 0) >= 1,
        },
        needsAttention: p.needsAttention ?? false,
        underused: p.underused ?? false,
        attentionGroup: Number(p.attentionGroup ?? 999),
      };
    });
  }, [overview]);

  const kpis = useMemo(() => {
    const src = overview && overview.kpis ? overview.kpis : null;
    if (src) {
      return {
        planned: Number(src.plannedHours ?? src.planned ?? 0),
        logged: Number(src.loggedHours ?? src.logged ?? 0),
        capacity: Number(src.capacityHours ?? src.capacity ?? 0),
        utilization: Number(src.utilization ?? 0),
        overloadedCount: Number(src.overloadedCount ?? 0),
        overdueTasksCount: Number(src.overdueTasksCount ?? 0),
      };
    }
    const planned = people.reduce((acc, p) => acc + (Number(p.plannedHours || 0) || 0), 0);
    const logged = people.reduce((acc, p) => acc + (Number(p.loggedHours || 0) || 0), 0);
    const capacity = people.reduce((acc, p) => acc + (Number(p.capacity || 0) || 0), 0);
    const utilization = capacity > 0 ? logged / capacity : 0;
    const overloadedCount = people.reduce((acc, p) => acc + (p.flags?.overloaded ? 1 : 0), 0);
    const overdueTasksCount = people.reduce((acc, p) => acc + (Number(p.overdueCount || 0) || 0), 0);
    return { planned, logged, capacity, utilization, overloadedCount, overdueTasksCount };
  }, [overview, people]);

  const filtered = useMemo(() => {
    const q = String(searchText || '').trim().toLowerCase();
    const statusFiltered = people.filter((p) => {
      if (status === 'all') return true;
      if (status === 'needsAttention') return Boolean(p.needsAttention);
      if (status === 'overloaded') return Boolean(p.flags?.overloaded);
      if (status === 'noLogs') return Boolean(p.flags?.noLogs);
      if (status === 'overdue') return Boolean(p.flags?.overdue);
      return true;
    });
    const searched = q
      ? statusFiltered.filter((p) => {
          const name = String(p.name || '').toLowerCase();
          const role = String(p.role || '').toLowerCase();
          return name.includes(q) || role.includes(q);
        })
      : statusFiltered;
    return searched.slice().sort(compareByKey(sortMode));
  }, [people, searchText, sortMode, status]);

  const headerBgStyle = {
    background: 'var(--ui-bg)',
    borderBottom: '1px solid var(--ui-border)',
  };

  return (
    <div className="flex flex-col gap-3">
      {/* Sticky header row */}
      <div className="sticky top-0 z-10 py-2" style={headerBgStyle}>
        <div className="flex items-center justify-between gap-3">
          <div className="text-xl font-semibold" style={{ color: 'var(--ui-text)' }}>People</div>
          <div className="flex items-center gap-2 flex-wrap justify-end">
            <select
              value={period}
              onChange={(e) => {
                const next = e.target.value;
                if (next === 'custom') ensureCustomRangeDefault();
                setPeriod(next);
              }}
              className="px-2 py-1 text-sm rounded border"
              style={{ borderColor: 'var(--ui-border)', color: 'var(--ui-text)', background: 'var(--ui-surface)' }}
              aria-label="Period"
              title="Period"
            >
              {PERIOD_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>

            {period === 'custom' && (
              <div className="flex items-center gap-2">
                <input
                  type="date"
                  value={customStart}
                  onChange={(e) => setCustomStart(e.target.value)}
                  className="px-2 py-1 text-sm rounded border"
                  style={{ borderColor: 'var(--ui-border)', color: 'var(--ui-text)', background: 'var(--ui-surface)' }}
                  aria-label="Custom start"
                  title="Custom start"
                />
                <input
                  type="date"
                  value={customEnd}
                  onChange={(e) => setCustomEnd(e.target.value)}
                  className="px-2 py-1 text-sm rounded border"
                  style={{ borderColor: 'var(--ui-border)', color: 'var(--ui-text)', background: 'var(--ui-surface)' }}
                  aria-label="Custom end"
                  title="Custom end"
                />
              </div>
            )}

            <select
              value={projectId}
              onChange={(e) => setProjectId(e.target.value)}
              className="px-2 py-1 text-sm rounded border"
              style={{ borderColor: 'var(--ui-border)', color: 'var(--ui-text)', background: 'var(--ui-surface)' }}
              aria-label="Project"
              title="Project"
            >
              <option value="all">All Projects</option>
              {projects.map((p) => (
                <option key={p.id} value={String(p.id)}>{p.name}</option>
              ))}
            </select>

            <input
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              placeholder="Search people…"
              className="px-2 py-1 text-sm rounded border"
              style={{
                borderColor: 'var(--ui-border)',
                color: 'var(--ui-text)',
                background: 'var(--ui-surface)',
                minWidth: 180,
              }}
              aria-label="Search people"
            />

            <button
              type="button"
              onClick={() => {}}
              className="h-8 w-8 inline-flex items-center justify-center rounded border"
              style={{ borderColor: 'var(--ui-border)', background: 'var(--ui-surface)', color: 'var(--ui-text)' }}
              aria-label="Export"
              title="Export"
            >
              <ExportIcon />
            </button>
          </div>
        </div>
      </div>

      {/* Organization Health strip */}
      <Card hover={false} padded={true}>
        <div className="flex items-center justify-between gap-3 flex-wrap">
          <div className="text-sm font-semibold" style={{ color: 'var(--ui-text)' }}>Organization Health</div>
          <div className="flex items-center gap-2 flex-wrap">
            <KpiTile label="Planned hours" value={formatHours(kpis.planned)} />
            <KpiTile label="Logged hours" value={formatHours(kpis.logged)} />
            <KpiTile label="Utilization %" value={formatPct(kpis.utilization)} />
            <KpiTile
              label="Overloaded count"
              value={String(kpis.overloadedCount)}
              onClick={() => setStatus('overloaded')}
            />
            <KpiTile
              label="Overdue tasks count"
              value={String(kpis.overdueTasksCount)}
              onClick={() => setStatus('overdue')}
            />
          </div>
        </div>
      </Card>

      {/* Segmented Status toggle */}
      <div>
        <SegmentedControl
          options={STATUS_TABS}
          value={status}
          onChange={setStatus}
          ariaLabel="Status"
          ariaRole="tablist"
          size="sm"
        />
      </div>

      {/* Results header + Sort + View */}
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <div className="text-sm font-semibold" style={{ color: 'var(--ui-text)' }}>People list</div>
          <InfoTooltip
            ariaLabel="Attention order info"
            text="Use Sort by to change ordering. Attention order is grouped by risk signals; no score is shown."
          />
        </div>

        <div className="flex items-center gap-2 flex-wrap justify-end">
          <div className="flex items-center gap-2">
            <div className="text-xs" style={{ color: 'var(--ui-text-muted)' }}>Sort by</div>
            <select
              value={sortMode}
              onChange={(e) => setSortMode(e.target.value)}
              className="px-2 py-1 text-xs rounded border"
              style={{
                borderColor: 'var(--ui-border)',
                color: 'var(--ui-text)',
                background: 'var(--ui-surface)',
              }}
              aria-label="Sort by"
              title="Sort by"
            >
              {SORT_OPTIONS.map((opt) => (
                <option key={opt.key} value={opt.key}>{opt.label}</option>
              ))}
            </select>
          </div>

          <div
            className="inline-flex rounded overflow-hidden border"
            style={{ borderColor: 'var(--ui-border)', background: 'var(--ui-surface)' }}
            aria-label="View mode"
          >
            <button
              type="button"
              onClick={() => setViewMode('table')}
              className="px-3 py-1 text-sm"
              style={{
                background: viewMode === 'table' ? 'var(--ui-primary-soft)' : 'transparent',
                color: 'var(--ui-text)',
              }}
            >
              Table
            </button>
            <button
              type="button"
              onClick={() => setViewMode('grid')}
              className="px-3 py-1 text-sm border-l"
              style={{
                borderLeftColor: 'var(--ui-border)',
                background: viewMode === 'grid' ? 'var(--ui-primary-soft)' : 'transparent',
                color: 'var(--ui-text)',
              }}
            >
              Grid
            </button>
          </div>
        </div>
      </div>

      {/* Attention Order table/grid */}
      {loadError ? (
        <Card hover={false}>
          <div className="text-sm" style={{ color: 'var(--ui-text)' }}>Failed to load people.</div>
          <div className="text-xs mt-1" style={{ color: 'var(--ui-text-muted)' }}>{loadError}</div>
          <button
            type="button"
            onClick={() => loadOverview()}
            className="mt-3 text-sm rounded px-3 py-2 border inline-flex items-center w-fit"
            style={{ borderColor: 'var(--ui-border)', background: 'var(--ui-surface)', color: 'var(--ui-text)' }}
          >
            Retry
          </button>
        </Card>
      ) : null}

      {viewMode === 'grid' ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {(loading ? Array.from({ length: 6 }).map((_, idx) => ({ __skeleton: true, id: `sk-${idx}` })) : filtered).map((p) => {
            if (p.__skeleton) {
              return (
                <Card key={p.id} className="text-left" role="group" hover={false}>
                  <div className="flex items-start gap-3">
                    <div className="h-9 w-9 rounded-full" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
                    <div className="flex-1 min-w-0">
                      <div className="h-4 w-2/3 rounded" style={{ background: 'var(--ui-surface2)' }} />
                      <div className="h-3 w-1/3 rounded mt-2" style={{ background: 'var(--ui-surface2)' }} />
                      <div className="flex gap-1 mt-3 flex-wrap">
                        <div className="h-5 w-16 rounded-full" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
                        <div className="h-5 w-20 rounded-full" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
                      </div>
                    </div>
                  </div>
                  <div className="mt-4">
                    <div className="h-3 w-full rounded" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
                  </div>
                  <div className="mt-3 grid grid-cols-2 gap-2">
                    <div className="h-12 rounded" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
                    <div className="h-12 rounded" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
                  </div>
                </Card>
              );
            }
            const utilPct = Math.round((p.utilization || 0) * 100);
            const personHref = `/people/${encodeURIComponent(p.id)}?period=${encodeURIComponent(period)}&project=${encodeURIComponent(projectId)}`;
            return (
              <Card key={p.id} className="text-left" role="group">
                <div
                  role="button"
                  tabIndex={0}
                  onClick={() => navigate(personHref)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                      e.preventDefault();
                      navigate(personHref);
                    }
                  }}
                  className="outline-none"
                >
                  <div className="flex items-start gap-3">
                  <PersonAvatar name={p.name} avatarUrl={p.avatarUrl} />
                  <div className="min-w-0 flex-1">
                    <div className="font-semibold truncate" style={{ color: 'var(--ui-text)' }}>{p.name}</div>
                    <div className="text-xs" style={{ color: 'var(--ui-text-muted)' }}>{p.role}</div>
                    <div className="mt-2 flex flex-wrap gap-1">
                      {p.flags.overloaded && <Chip title="Overloaded">Overloaded</Chip>}
                      {!p.flags.overloaded && p.flags.nearCapacity && <Chip title="Near capacity">Near capacity</Chip>}
                      {p.flags.overdue && <Chip title="Overdue">Overdue</Chip>}
                      {p.flags.noLogs && (
                        <Chip
                          title="No logs"
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/calendar?person=${encodeURIComponent(p.id)}&period=${encodeURIComponent(period)}`);
                          }}
                        >
                          No logs ({p.noLogDays}d)
                        </Chip>
                      )}
                      {p.underused && <Chip title="Underused">Underused</Chip>}
                      {!p.flags.overloaded && !p.flags.nearCapacity && !p.flags.overdue && !p.flags.noLogs && !p.underused && (
                        <Chip title="No flags">—</Chip>
                      )}
                    </div>
                  </div>
                  </div>

                  <div className="mt-3">
                    <div className="flex items-center justify-between text-xs" style={{ color: 'var(--ui-text-muted)' }}>
                      <span>Utilization</span>
                      <span style={{ color: 'var(--ui-text)' }}>{utilPct}%</span>
                    </div>
                    <div className="mt-1">
                      <ProgressBar value={p.utilization} />
                    </div>
                  </div>

                  <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                    <div className="rounded p-2" style={{ border: '1px solid var(--ui-border)', background: 'var(--ui-surface2)' }}>
                      <div style={{ color: 'var(--ui-text-muted)' }}>Planned/Capacity</div>
                      <div className="font-semibold" style={{ color: 'var(--ui-text)' }}>
                        {formatHours(p.plannedHours)} / {formatHours(p.capacity)}
                      </div>
                    </div>
                    <button
                      type="button"
                      className="rounded p-2 text-left"
                      style={{
                        border: '1px solid var(--ui-border)',
                        background: 'var(--ui-surface2)',
                        color: 'var(--ui-text)',
                        opacity: p.overdueCount > 0 ? 1 : 0.6,
                        cursor: p.overdueCount > 0 ? 'pointer' : 'default',
                      }}
                      onClick={(e) => {
                        e.stopPropagation();
                        if (p.overdueCount <= 0) return;
                        navigate(
                          `/tasks?assignee=${encodeURIComponent(p.id)}&status=overdue&period=${encodeURIComponent(period)}`
                        );
                      }}
                      aria-label={`Overdue: ${p.overdueCount}`}
                      title={p.overdueCount > 0 ? 'Open overdue tasks' : 'No overdue tasks'}
                    >
                      <div style={{ color: 'var(--ui-text-muted)' }}>Overdue</div>
                      <div className="font-semibold" style={{ color: p.overdueCount > 0 ? 'var(--ui-danger)' : 'var(--ui-text)' }}>
                        {p.overdueCount}
                      </div>
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      ) : (
        <Card hover={false} padded={false}>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
              <thead>
                <tr style={{ background: 'var(--ui-surface2)' }}>
                  <th className="text-left px-3 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>
                    <span className="inline-flex items-center gap-2">
                      <span>Name</span>
                      <InfoTooltip
                        ariaLabel="Name column info"
                        text="Click a person to open detailed page."
                      />
                    </span>
                  </th>
                  <th className="text-left px-3 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>
                    <span className="inline-flex items-center gap-2">
                      <span>Attention Flags</span>
                      <InfoTooltip
                        ariaLabel="Attention flags info"
                        text="Overloaded — planned work exceeds capacity.\nNear capacity — workload close to limit.\nOverdue — person has overdue tasks.\nNo logs — missing time entries.\nUnderused — low workload or low utilization."
                        maxWidthClassName="w-96"
                      />
                    </span>
                  </th>
                  <th className="text-left px-3 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>Utilization</th>
                  <th className="text-left px-3 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>Planned/Capacity</th>
                  <th className="text-left px-3 py-2 font-semibold" style={{ color: 'var(--ui-text)', borderBottom: '1px solid var(--ui-border)' }}>Overdue</th>
                  <th className="px-3 py-2" style={{ borderBottom: '1px solid var(--ui-border)' }} />
                </tr>
              </thead>
              <tbody>
                {(loading ? Array.from({ length: 6 }).map((_, idx) => ({ __skeleton: true, id: `sk-${idx}` })) : filtered).map((p) => {
                  if (p.__skeleton) {
                    return (
                      <tr key={p.id}>
                        <td className="px-3 py-3" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                          <div className="flex items-center gap-3">
                            <div className="h-9 w-9 rounded-full" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
                            <div className="min-w-0 flex-1">
                              <div className="h-4 w-40 rounded" style={{ background: 'var(--ui-surface2)' }} />
                              <div className="h-3 w-20 rounded mt-2" style={{ background: 'var(--ui-surface2)' }} />
                            </div>
                          </div>
                        </td>
                        <td className="px-3 py-3" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                          <div className="flex gap-1 flex-wrap">
                            <div className="h-5 w-20 rounded-full" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
                            <div className="h-5 w-16 rounded-full" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
                          </div>
                        </td>
                        <td className="px-3 py-3" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                          <div className="h-3 w-48 rounded" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
                        </td>
                        <td className="px-3 py-3" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                          <div className="h-4 w-24 rounded" style={{ background: 'var(--ui-surface2)' }} />
                        </td>
                        <td className="px-3 py-3" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                          <div className="h-6 w-12 rounded-full" style={{ background: 'var(--ui-surface2)', border: '1px solid var(--ui-border)' }} />
                        </td>
                        <td className="px-3 py-3" style={{ borderBottom: '1px solid var(--ui-border)' }} />
                      </tr>
                    );
                  }
                  const utilPct = Math.round((p.utilization || 0) * 100);
                  const personHref = `/people/${encodeURIComponent(p.id)}?period=${encodeURIComponent(period)}&project=${encodeURIComponent(projectId)}`;
                  return (
                    <tr
                      key={p.id}
                      className="cursor-pointer"
                      onClick={() => navigate(personHref)}
                    >
                      <td className="px-3 py-2" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                        <div className="flex items-center gap-3">
                          <PersonAvatar name={p.name} avatarUrl={p.avatarUrl} />
                          <div className="min-w-0">
                            <div className="font-semibold truncate" style={{ color: 'var(--ui-text)' }}>{p.name}</div>
                            <div className="text-xs" style={{ color: 'var(--ui-text-muted)' }}>{p.role}</div>
                          </div>
                        </div>
                      </td>
                      <td className="px-3 py-2" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                        <div className="flex flex-wrap gap-1">
                          {p.flags.overloaded && <Chip title="Overloaded">Overloaded</Chip>}
                          {!p.flags.overloaded && p.flags.nearCapacity && <Chip title="Near capacity">Near capacity</Chip>}
                          {p.flags.overdue && <Chip title="Overdue">Overdue</Chip>}
                          {p.flags.noLogs && (
                            <Chip
                              title="No logs"
                              onClick={(e) => {
                                e.stopPropagation();
                                navigate(`/calendar?person=${encodeURIComponent(p.id)}&period=${encodeURIComponent(period)}`);
                              }}
                            >
                              No logs ({p.noLogDays}d)
                            </Chip>
                          )}
                          {p.underused && <Chip title="Underused">Underused</Chip>}
                          {!p.flags.overloaded && !p.flags.nearCapacity && !p.flags.overdue && !p.flags.noLogs && !p.underused && (
                            <Chip title="No flags">—</Chip>
                          )}
                        </div>
                      </td>
                      <td className="px-3 py-2" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                        <div className="flex flex-col gap-1" style={{ minWidth: 180 }}>
                          <div className="flex items-center justify-between text-xs" style={{ color: 'var(--ui-text-muted)' }}>
                            <span>{utilPct}%</span>
                            <span style={{ color: 'var(--ui-text)' }}>{formatHours(p.loggedHours)} / {formatHours(p.capacity)}</span>
                          </div>
                          <ProgressBar value={p.utilization} />
                        </div>
                      </td>
                      <td className="px-3 py-2" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                        <span style={{ color: 'var(--ui-text)' }}>{formatHours(p.plannedHours)} / {formatHours(p.capacity)}</span>
                      </td>
                      <td className="px-3 py-2" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                        <button
                          type="button"
                          className="inline-flex items-center justify-center min-w-[40px] px-2 py-1 rounded-full text-xs border"
                          style={{
                            borderColor: p.overdueCount > 0 ? 'var(--ui-danger)' : 'var(--ui-border)',
                            color: p.overdueCount > 0 ? 'var(--ui-danger)' : 'var(--ui-text)',
                            background: 'var(--ui-surface)',
                            opacity: p.overdueCount > 0 ? 1 : 0.6,
                            cursor: p.overdueCount > 0 ? 'pointer' : 'default',
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (p.overdueCount <= 0) return;
                            navigate(
                              `/tasks?assignee=${encodeURIComponent(p.id)}&status=overdue&period=${encodeURIComponent(period)}`
                            );
                          }}
                          aria-label={`Open overdue tasks for ${p.name}`}
                          title={p.overdueCount > 0 ? 'Open overdue tasks' : 'No overdue tasks'}
                        >
                          {p.overdueCount}
                        </button>
                      </td>
                      <td className="px-3 py-2" style={{ borderBottom: '1px solid var(--ui-border)' }}>
                        <span style={{ color: 'var(--ui-text-muted)' }}>
                          <ChevronRightIcon />
                        </span>
                      </td>
                    </tr>
                  );
                })}

                {filtered.length === 0 && (
                  <tr>
                    <td
                      colSpan={6}
                      className="px-3 py-6 text-sm"
                      style={{ color: 'var(--ui-text-muted)' }}
                    >
                      No people match the current filters.
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}
